// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, type ReactNode } from 'react';

import type { TaskType, TaskTypeGroup, ShopConfiguration } from '@evolvemep/api-ts';
import { Button, Divider, Flex } from '@mantine/core';

import { WrappedModal, type WrappedModalProps } from 'components/Mantine/Modals/WrappedModal';
import { useRemountKey } from 'hooks/useRemountKey';
import { TaskTypeModalProvider } from 'modules/Shop/Tasks/TaskTypes/modalRegistries';
import { TaskGroupPanel } from 'modules/Shop/Tasks/TaskTypes/TaskGroupPanel';
import { TaskGroupTaskList } from 'modules/Shop/Tasks/TaskTypes/TaskGroupTaskList';
import type { TaskGroupFilter } from 'modules/Shop/Tasks/TaskTypes/types';
import { useSelectedTasks } from 'modules/Shop/Tasks/TaskTypes/useSelectedTasks';

type Props = {
  opened: boolean;
  onClose: () => void;
  /** Renders the action shown beside the Cancel button, given the currently selected task types. */
  renderAction: (selectedTasks: TaskType[]) => ReactNode;
  shopConfiguration?: ShopConfiguration;
} & Pick<WrappedModalProps, 'title' | 'subtitle' | 'miniHeader' | 'keepMounted'>;

const TaskPickerContents = ({
  onClose,
  renderAction,
  shopConfiguration,
}: Pick<Props, 'onClose' | 'renderAction' | 'shopConfiguration'>) => {
  const [selectedTasks, updateSelectedTasks] = useSelectedTasks();
  const [groupFilter, setGroupFilter] = useState<TaskGroupFilter>('all');
  const [taskGroupPanelKey, remountTaskGroupPanel] = useRemountKey('taskGroupPanel');
  const [taskGroupTaskListKey, remountTaskGroupTaskList] = useRemountKey('taskGroupTaskList');
  const shopConfigurationId = shopConfiguration?.shopConfigurationId;

  return (
    <Flex direction="column" h="100%" style={{ overflow: 'hidden' }}>
      <Flex flex={1} style={{ overflow: 'hidden' }}>
        <TaskGroupPanel
          key={taskGroupPanelKey}
          selectedTasks={selectedTasks}
          groupFilter={groupFilter}
          onGroupFilterChange={setGroupFilter}
          onTaskGroupUpdated={(updated: TaskTypeGroup) => {
            setGroupFilter((prev) =>
              typeof prev === 'object' && prev.taskTypeGroupId === updated.taskTypeGroupId ? updated : prev,
            );
            remountTaskGroupTaskList();
          }}
          onTaskGroupDeleted={(deleted: TaskTypeGroup) => {
            // if the currently selected group is the one being deleted, reset the filter to 'all'
            setGroupFilter((prev) =>
              typeof prev === 'object' && prev.taskTypeGroupId === deleted.taskTypeGroupId ? 'all' : prev,
            );
            updateSelectedTasks({ type: 'taskTypeGroupDeleted', taskTypeGroup: deleted });
            remountTaskGroupTaskList();
          }}
        />
        <Divider orientation="vertical" />
        <TaskGroupTaskList
          key={taskGroupTaskListKey}
          selectedTasks={selectedTasks}
          updateSelectedTasks={updateSelectedTasks}
          groupFilter={groupFilter}
          shopConfigurationId={shopConfigurationId}
          onTaskTypesChange={remountTaskGroupPanel}
        />
      </Flex>
      <Divider />
      <Flex justify="flex-end" gap="sm" p="md">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        {renderAction(selectedTasks)}
      </Flex>
    </Flex>
  );
};

/** Modal for browsing task groups and selecting task types, with a caller-supplied action for the selection. */
export const TaskPickerModal = ({ opened, onClose, renderAction, shopConfiguration, ...modalProps }: Props) => (
  <TaskTypeModalProvider>
    <WrappedModal
      {...modalProps}
      opened={opened}
      onClose={onClose}
      hideModalButtons
      size="xxl"
      tall
      bodyProps={{ p: 0 }}
    >
      <TaskPickerContents onClose={onClose} renderAction={renderAction} shopConfiguration={shopConfiguration} />
    </WrappedModal>
  </TaskTypeModalProvider>
);
